/** Fetch on open/focus and after writes. Every merge save stamps the backend config version. */
import { useCallback, useEffect, useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from 'config/firebase';
import { useFirebaseAuth } from 'context/FirebaseAuthContext';
import {
  SETTINGS_COLLECTION,
  SETTINGS_DOC,
  DEFAULT_SETTINGS
} from '../config/rateShopConstants';
import { useRequestGuard, useRefreshOnVisible } from './useRequestGuard';

export const mergeSettings = raw => ({
  ...DEFAULT_SETTINGS,
  ...(raw || {}),
  carrierAccounts: { ...(raw?.carrierAccounts || {}) },
  groups: { ...DEFAULT_SETTINGS.groups, ...(raw?.groups || {}) },
  tags: { ...DEFAULT_SETTINGS.tags, ...(raw?.tags || {}) },
  sweep: { ...DEFAULT_SETTINGS.sweep, ...(raw?.sweep || {}) }
});

export function useRateShopSettings() {
  const [user, authLoading] = useFirebaseAuth();
  const [settings, setSettings] = useState(null);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const guard = useRequestGuard();
  const refresh = useCallback(async () => {
    if (!guard.mounted()) return;
    const token = guard.begin();
    if (authLoading || !user) {
      setSettings(null);
      setExists(false);
      setLoading(authLoading);
      setError(null);
      return null;
    }
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, SETTINGS_COLLECTION, SETTINGS_DOC));
      if (!guard.current(token)) return null;
      const result = mergeSettings(snap.exists() ? snap.data() : {});
      setExists(snap.exists());
      setSettings(result);
      setError(null);
      return result;
    } catch (err) {
      if (guard.current(token)) setError(err);
      return null;
    } finally {
      if (guard.current(token)) setLoading(false);
    }
  }, [authLoading, user, guard]);
  useEffect(() => {
    refresh();
    return guard.invalidate;
  }, [refresh, guard]);
  useRefreshOnVisible(refresh);
  const saveSettings = useCallback(
    async patch => {
      if (!user?.email) throw new Error('Not signed in');
      setSaving(true);
      try {
        await setDoc(
          doc(db, SETTINGS_COLLECTION, SETTINGS_DOC),
          { ...patch, updatedAt: serverTimestamp(), updatedBy: user.email },
          { merge: true }
        );
        return await refresh();
      } finally {
        if (guard.mounted()) setSaving(false);
      }
    },
    [user, refresh, guard]
  );
  return { settings, exists, loading, error, saving, saveSettings, refresh };
}
